import type { CodeBlockSuggestion, CodeBlockSuggestionError } from '@aline/common';
import { aiMimeTypes, parseJsonlToSuggestionList } from '@aline/common';
import { remark } from 'remark';
import { visit } from 'unist-util-visit';

import type { SuggestionStatus } from '../../chat-types';

export type SuggestionWithStatus = {
  status: SuggestionStatus;
  suggestion: CodeBlockSuggestion;
  type: 'suggestion';
};

export type ErrorWithStatus = {
  error: CodeBlockSuggestionError;
  type: 'error';
};

export type SuggestionsWithStatus = (SuggestionWithStatus | ErrorWithStatus)[];

/**
 * Collects suggestion entries from every `jsonl-suggestion-list` code block
 * in a message's markdown — the legacy-format sibling of
 * `collectRedlineStatuses`.
 *
 * A message can contain multiple suggestion-list blocks (the model's
 * reasoning can close and reopen the fence), so entries are accumulated
 * across all blocks into one array. The caller must write them to the
 * reactive variable in a single set — setting once per block would let the
 * last block's entries replace the earlier blocks' statuses.
 *
 * Resolving an edit's status needs the live editor and document, so it stays
 * with the caller via `getEditStatus`.
 */
export function collectSuggestionsWithStatus(
  completionText: string,
  options: { getEditStatus: (suggestion: CodeBlockSuggestion) => SuggestionStatus }
): { hasSuggestionListBlock: boolean; suggestionsWithStatus: SuggestionsWithStatus } {
  let hasSuggestionListBlock = false;
  const suggestionsWithStatus: SuggestionsWithStatus = [];

  visit(remark().parse(completionText), 'code', node => {
    if (node.lang !== aiMimeTypes.jsonlSuggestionList) return;
    hasSuggestionListBlock = true;

    for (const item of parseJsonlToSuggestionList(node.value)) {
      if (item.type === 'edit') {
        suggestionsWithStatus.push({
          status: options.getEditStatus(item),
          suggestion: item,
          type: 'suggestion',
        });
      } else {
        suggestionsWithStatus.push({ error: item, type: 'error' });
      }
    }
  });

  return { hasSuggestionListBlock, suggestionsWithStatus };
}
